import { useContext, useEffect, useRef, useState } from "react";
import Loading from "../../../../main/components/Loading";
import { Screen } from "../../../../main/components/screen/Screen";
import { CenterOperationsData } from "../../types";
// import EmptyStateNotFound from "../../../clients/components/EmptyState";
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import RadarIcon from '@mui/icons-material/Radar';
import { SlEnergy } from "react-icons/sl";
//import { LuGauge } from "react-icons/lu";
import { GoPulse } from "react-icons/go";
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import CardBox from "../../../../main/components/CardBox";
import RopTable, { TableItem, TableRowType } from "../../../../main/components/RopTable/RopTable";
import { EquipmentsStatus, EquipmentsStatusProps } from "./EquipmentsStatus";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import MovingIcon from '@mui/icons-material/Moving';
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import BaseButton from "../../../../main/components/BaseButton";
//import { operationsApi } from "../../../../services/api";
import { MainContext } from "../../../../main/context/MainContextProvider";
import { alarmsApi } from "../../../../services/api";
//import BackupTableIcon from '@mui/icons-material/BackupTable';
//import MapIcon from '@mui/icons-material/Map';
import KPIComponent, { KPIComponentProps } from "./KPIComponent";
import ActivePerformanceCard, { ActivePerformanceCardProps } from "./ActivePerformanceCard";
import RecentEventsCard, { RecentEventsProps } from "./RecentEventsCard";
//import SuggestionCard, { SuggestionCardProps } from "./SuggestionCard";
import { downloadAtivosDaUsinaCsv } from "../../helpers/exportAtivosDaUsinaToCsv";
import { formatDurationFromTimeSpan } from "../../../shared/helpers/formatters/duration";
import { formatDateTimeShort, formatTime } from "../../../shared/helpers/formatters/datetime";
import { formatEnergyParts, formatIrradiance, formatPower, formatPowerParts, formatTemperature } from "../../../shared/helpers/formatters/energy";
import { formatPercentParts } from "../../../shared/helpers/formatters/percentage";
import { ropCardToCenterOperationsData } from "../../helpers/ropCardToCenterOperationsData";
import { MonitoringSessionConnection } from "../../../dashboard/helpers/MonitoringSessionConnection";
import { useNavigate } from "react-router-dom";
import { useClientClusterUsinaFilterContext } from "../../../../main/context/ClientClusterUsinaFilterContextProvider";
import dayjs, { Dayjs } from "dayjs";
import DatePickerLocalizationAdapter from "../../../../main/context/DatePickerLocalizationAdapter";
import { DatePicker } from "@mui/x-date-pickers";
import { usinasApi } from "../../../../services/api";
import { formatDateRequest } from "../../../../main/utils/utils";
import type { UsinaByClient } from "../../../usinas/types";

function operationalAlarmSeverityToLabel(severity: number): string {
    switch (severity) {
        case 1:
            return "Leve";
        case 2:
            return "Atencao";
        case 3:
            return "Critico";
        default:
            return "Nao Informado";
    }
}

type UnknownRecord = Record<string, unknown>;

function rowFromOperationalAlarm3002(
    alarm: UnknownRecord,
    exportDateBR: string,
    fallbackPlanta: string,
): (string | number)[] {
    const usinaName = alarm.usinaName ?? alarm.UsinaName;
    const usinaCode = alarm.usinaCode ?? alarm.UsinaCode;
    const code = alarm.code ?? alarm.Code;
    const area = alarm.area ?? alarm.Area;
    const severity = Number(alarm.severity ?? alarm.Severity ?? -1);
    const isActiveRaw = alarm.isActive ?? alarm.IsActive;
    const isActive = Boolean(isActiveRaw);
    const message = alarm.message ?? alarm.Message;
    const triggeredAt = alarm.triggeredAt ?? alarm.TriggeredAt;
    const alarmDuration = alarm.alarmDuration ?? alarm.AlarmDuration;
    const planta =
        typeof usinaName === "string" && usinaName.length > 0
            ? usinaName
            : fallbackPlanta || String(usinaCode ?? "");
    return [
        exportDateBR,
        planta,
        String(code ?? ""),
        String(area ?? ""),
        operationalAlarmSeverityToLabel(severity),
        isActive ? "Ativo" : "Inativo",
        String(message ?? ""),
        formatDateTimeShort(
            triggeredAt == null ? "" : typeof triggeredAt === "string" ? triggeredAt : String(triggeredAt),
        ),
        formatDurationFromTimeSpan(
            alarmDuration == null ? "" : typeof alarmDuration === "string" ? alarmDuration : String(alarmDuration),
        ),
    ];
}

function rowsFromOperationalCard3002(
    card: UnknownRecord,
    exportDateBR: string,
    fallbackPlanta: string,
): (string | number)[][] {
    const c = card as UnknownRecord;
    const alarmsRaw = c.currentAlarms ?? c.CurrentAlarms;
    if (!Array.isArray(alarmsRaw) || alarmsRaw.length === 0) return [];
    return alarmsRaw.map((a) =>
        rowFromOperationalAlarm3002(a as UnknownRecord, exportDateBR, fallbackPlanta),
    );
}

async function fetchOperationalDashboardHistoryRows(
    usina: UsinaByClient,
    dateRequest: string,
    exportDateBR: string,
): Promise<(string | number)[][]> {
    const ropInputId = usina.usinaIds.ropInputId;
    const fallbackPlanta = usina.name ?? "";
    const envelope = await usinasApi.getDashboardHistoryCentroOperacoesSolarEventos(
        ropInputId,
        dateRequest,
    );
    const currentAlarms = envelope.data.currentAlarms ?? [];

    return rowsFromOperationalCard3002(
        { currentAlarms } as UnknownRecord,
        exportDateBR,
        fallbackPlanta,
    );
}

export default function OperationalScreen() {
    const navigate = useNavigate();

    const {
        theme,
        user,
        setToastNotifications,
    } = useContext(MainContext);
    const {
        selectedClientFilter,
        selectedCluster,
        selectedUsinaFilter,
        availableUsinas,
    } = useClientClusterUsinaFilterContext();

    const [centerOperationsData, setCenterOperationsData] = useState<CenterOperationsData | null>(null);
    const [search, setSearch] = useState("");
    const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
    const [selectedExportDate, setSelectedExportDate] = useState<Dayjs | null>(dayjs());
    const [isExportingHistory, setIsExportingHistory] = useState(false);

    const monitoringSessionConnectionRef = useRef<MonitoringSessionConnection>();

    const ativosDaUsina = centerOperationsData?.ativosDaUsina ?? [];
    const isUpdating = !centerOperationsData?.isRopCard3001Loaded || !centerOperationsData?.isRopCard3002Loaded;

    const setCenterOperationsUpdating = () => {
        setCenterOperationsData((prevCoData) =>
            prevCoData
                ? {
                    ...prevCoData,
                    isRopCard3001Loaded: false,
                    isRopCard3002Loaded: false,
                }
                : prevCoData,
        );
    };

    useEffect(() => {
        if (!user?.userAccessToken) {
            console.warn("Conexão WebSocket não iniciada: token de acesso do usuário indisponível.");
            return;
        }

        const enqueueNotification = (text: string, severity: "info" | "warning" | "error" | "success") => {
            setToastNotifications(prev => ({
                isAdding: true,
                notifications: [
                    ...prev.notifications,
                    {
                        text: text,
                        severity: severity,
                    },
                ],
            }));
        }

        const ws = new MonitoringSessionConnection(user.userAccessToken);
        monitoringSessionConnectionRef.current = ws;

        ws.addListener("connected", () => {
            console.debug("MonitoringSessionConnection conectado");
            enqueueNotification("Conectado à sessão de monitoramento!", "success");
        });
        ws.addListener("reconnecting", (retryCount: number) => {
            console.debug("MonitoringSessionConnection reconectando", retryCount);
            enqueueNotification(
                retryCount === 0
                    ? "Conectando à sessão de monitoramento..."
                    : `Reconectando à sessão de monitoramento... (${retryCount}ª tentativa)`,
                "warning"
            );
            setCenterOperationsUpdating();
        });
        ws.addListener("disconnected", () => {
            console.debug("MonitoringSessionConnection desconectado");
            setCenterOperationsUpdating();
        });
        ws.addListener("error", (errorDetail) => {
            console.error("MonitoringSessionConnection erro:", errorDetail);
            enqueueNotification(errorDetail.displayMessage, "error");
        });
        ws.addListener("ropCardUpdate", (ropCardUpdate) => {
            setCenterOperationsData(prevCoData => ropCardToCenterOperationsData(ropCardUpdate, prevCoData));
        });

        return () => {
            console.debug("Desconectando MonitoringSessionConnection");
            ws.destroy();
        }
    }, [user]);

    useEffect(() => {
        if (!monitoringSessionConnectionRef.current) {
            console.warn("monitoringSessionConnectionRef não está inicializado.");
            return;
        }

        monitoringSessionConnectionRef.current.updateMonitoringScope({
            clientId: selectedClientFilter?.id || null,
            clusterId: selectedCluster?.id || null,
            usinaId: selectedUsinaFilter?.id || null,
        });
        setCenterOperationsUpdating();

    }, [selectedClientFilter?.id, selectedCluster?.id, selectedUsinaFilter?.id])

    const formattedPotenciaAtualParts = formatPowerParts(centerOperationsData?.kpiData.potenciaAtual ?? null);
    const formattedEnergiaGeradaParts = formatEnergyParts(centerOperationsData?.kpiData.energiaGerada ?? null);
    const formattedPerformanceParts = formatPercentParts(centerOperationsData?.kpiData.performance ?? null, "ratio");
    const formattedDisponibilidadeParts = formatPercentParts(centerOperationsData?.kpiData.disponibilidade ?? null, "hundred");

    const desvioDeMeta = centerOperationsData?.kpiData.desvioDeMeta ?? 0;

    const IGUData: KPIComponentProps[] = [
        {
            icon: <PowerSettingsNewIcon sx={{ color: "#8F969E" }} />,
            sufixx: formattedPotenciaAtualParts.unit,
            title: "Potência Atual",
            value: formattedPotenciaAtualParts.value,
            gridSize: { xs: 6, md: 2.4 }
        },
        {
            icon: <RadarIcon sx={{ color: desvioDeMeta <= 0 ? "#10B981" : "#EF4444" }} />,
            sufixx: "%",
            title: "Desvio de Meta",
            value: (
                <Box sx={{ display: "flex", alignItems: "center", gap: "2px", justifyContent: "center" }}>
                    <Typography sx={{ fontSize: "18px", color: desvioDeMeta <= 0 ? "#10B981" : "#EF4444" }}>
                        {desvioDeMeta < 0 ? "▲" : "▼"}
                    </Typography>
                    <Typography
                        sx={{ fontSize: "18px", color: desvioDeMeta <= 0 ? "#10B981" : "#EF4444", fontWeight: "900" }}
                    >
                        {desvioDeMeta < 0 ? "+" : ""}{Math.abs(Number(desvioDeMeta)).toFixed(1)}
                    </Typography>
                </Box >
            ),
            gridSize: { xs: 6, md: 2.4 }
        },
        {
            icon: <SlEnergy style={{ color: "#8F969E" }} />,
            sufixx: formattedEnergiaGeradaParts.unit,
            title: "Energia Gerada",
            value: formattedEnergiaGeradaParts.value,
            gridSize: { xs: 6, md: 2.4 }
        },
        {
            icon: <MovingIcon style={{ color: "#8F969E" }} />,
            sufixx: formattedPerformanceParts.unit,
            title: "Performance Ratio",
            value: formattedPerformanceParts.value,
            gridSize: { xs: 6, md: 2.4 }
        },
        {
            icon: <AccessTimeOutlinedIcon sx={{ color: "#8F969E" }} />,
            sufixx: formattedDisponibilidadeParts.unit,
            title: "Disponibilidade",
            value: formattedDisponibilidadeParts.value,
            gridSize: { xs: 6, md: 2.4 },
        },
        // {
        //     icon: <LuGauge style={{ color: "#8F969E" }} />,
        //     sufixx: "/100",
        //     title: "IGU",
        //     value: centerOperationsData?.kpiData.IGU.toFixed(1) ?? "-",
        //     status: "warning"
        // },
    ]

    const activePerformanceCards: ActivePerformanceCardProps[] = centerOperationsData?.performanceData.map((item) => {
        return {
            name: item.name,
            status: item.status,
            sector: item.sector,
            message: item.message,
            potencia: formatPower(item.potencia),
            performanceRatio: item.performanceRatio ?? null,
            irradiancia: formatIrradiance(item.irradiancia),
            irradianciaRaw: item.irradiancia,
            irradianciaMaximaRaw: item.irradianciaMaxima,
            temperatura: formatTemperature(item.temperatura),
            hasCriticalEvent: centerOperationsData?.ativosDaUsina.some(active => active.status === "Critico" && active.planta === item.name && active.isActive),
        }
    }) || []

    const recentEvents: RecentEventsProps[] = centerOperationsData?.recentEventsData.map((item) => {
        return {
            text: item.text,
            hour: formatTime(item.hour),
            status: item.status,
        }
    }) || [];

    // const suggestionEvents: SuggestionCardProps[] = centerOperationsData?.suggestionEventsData.map((item) => {
    //     return {
    //         severity: item.severity,
    //         text: item.text,
    //         onExcuteClick: () => { },
    //         onDetailClick: () => { }
    //     }
    // }) || [];

    const equipmentInfos: EquipmentsStatusProps[] = centerOperationsData?.equipamentosInfoData.
        sort((a, b) => a.title.localeCompare(b.title)).
        map((item) => {
            const isItemWarning = !item.isTrafo ? item.percent !== 1 : item.notifications[0].items.length > 0;
            const itemColor = isItemWarning ? "warning.main" : "success.main";
            const itemIcon = isItemWarning ? (
                <WarningAmberIcon
                    sx={{ color: itemColor, fontSize: 22, marginRight: 1 }}
                />
            ) : (
                <TaskAltIcon
                    sx={{ color: itemColor, fontSize: 22, marginRight: 1 }}
                />
            );
            const sensorOnline = item.online - item.maintenance < 0 ? 0 : item.online - item.maintenance;
            const online = item.title == "Sensores" ? sensorOnline : item.online;
            return {
                title: item.title,
                online: online,
                offline: item.offline,
                manutencao: item.maintenance,
                performance: item.performance,
                totalUnits: item.totalUnits,
                percent: item.percent * 100,
                updatedAt: formatTime(item.updatedAt),
                icon: itemIcon,
                barColor: itemColor,
                hidePerformance: item.hidePerformance,
                isTrafo: item.isTrafo,
                operationalStatuses: item.operationalStatuses,
                notifications: item.notifications.map((notification) => {
                    return {
                        title: notification.title,
                        type: notification.type,
                        items: notification.items,
                    }
                })
            }
        }) || [];

    const getStatusChip = (status: string) => {
        switch (status) {
            case "Critico":
                return <Chip label="Crítico" sx={{ bgcolor: "rgba(244,67,54,0.08)", color: "error.main" }} size="small" />;
            case "Atencao":
                return <Chip label="Atenção" sx={{ bgcolor: "rgba(255,193,7,0.08)", color: "warning.main" }} size="small" />;
            case "Leve":
                return <Chip label="Leve" sx={{ bgcolor: "rgba(76,175,80,0.08)", color: "#C6C6C6" }} size="small" />;
            default:
                return <Chip label={status} size="small" />;
        }
    };

    const getActivityChip = (isActive: boolean) => {
        if (isActive) {
            return <Chip label="Ativo" sx={{ bgcolor: "rgba(76,175,80,0.08)", color: "success.main" }} size="small" />;
        } else {
            return <Chip label="Inativo" sx={{ bgcolor: "rgba(158,158,158,0.08)", color: "#8F969E" }} size="small" />;
        }
    };

    const [statusFilter, setStatusFilter] = useState<string[]>(["ativo", "critico"]);

    const handleChangeStatusFilter = (_: any, newStatus: string[]) => {
        const hasAtivo = newStatus.includes("ativo");
        const hasInativo = newStatus.includes("inativo");

        if (hasAtivo && hasInativo) {
            const selectedNow = newStatus.filter((status) => !statusFilter.includes(status));
            const lastSelected = selectedNow[selectedNow.length - 1];

            if (lastSelected === "ativo") {
                setStatusFilter(newStatus.filter((status) => status !== "inativo"));
                return;
            }

            if (lastSelected === "inativo") {
                setStatusFilter(newStatus.filter((status) => status !== "ativo"));
                return;
            }
        }

        setStatusFilter(newStatus);
    }

    const gotToAlarmDetails = (alarmCode: string) => {
        navigate(`/alarm/matriz/${alarmCode}`, {
            state: {
                navigateFrom: "center-operations",
            },
        });
    }

    const goToServiceRequest = async (item: CenterOperationsData["ativosDaUsina"][number]) => {
        let alarmSolutions = "";

        try {
            const alarmDetails = await alarmsApi.getAlarmDetails(item.alarmCode);
            alarmSolutions = alarmDetails?.solutions ?? "";
        } catch {
            // Keep navigation available even if details API fails.
            alarmSolutions = "";
        }

        navigate("/service-request/cadastro", {
            state: {
                alarmUsinaId: item.alarmUsinaId,
                alarmCode: item.alarmCode,
                alarmUg: item.alarmUg,
                alarmArea: item.alarmArea,
                alarmString: item.alarmString,
                alarmUsinaName: item.planta,
                alarmDescricao: `${item.alarmCode}${item.alarmMessage ? " - " + item.alarmMessage : ""}`,
                alarmSolutions,
                alarmLastActiveDate: item.alarmLastActiveDate,
                alarmMessage: item.alarmMessage,
                navigateFrom: "center-operations",
            },
        });
    }

    // const [filteredAtivosDaUsina, setFilteredAtivosDaUsina] = useState<any[]>([]);

    const getTableItemsFiltered = () => {
        let severityFilters = []
        let filteredRows = ativosDaUsina || [];

        if (search.trim() !== "") {
            const searchLower = search.toLowerCase().trim();
            filteredRows = filteredRows.filter((item) => {
                const nomeDoAtivo = (item.nomeDoAtivo || "").toLowerCase();
                const planta = (item.planta || "").toLowerCase();
                return nomeDoAtivo.includes(searchLower) || planta.includes(searchLower);
            });
        }

        if (statusFilter.length > 0) {
            if (statusFilter.includes("leve")) {
                severityFilters.push("Leve")
            }
            if (statusFilter.includes("atencao")) {
                severityFilters.push("Atencao")
            }
            if (statusFilter.includes("critico")) {
                severityFilters.push("Critico")
            }

            if (severityFilters.length > 0) {
                filteredRows = filteredRows.filter((item) =>
                    severityFilters.includes(item.status)
                );
            }
        }

        if (statusFilter.length > 0) {
            if (statusFilter.includes("ativo")) {
                filteredRows = filteredRows.filter((item) => item.isActive);
            }
            if (statusFilter.includes("inativo")) {
                filteredRows = filteredRows.filter((item) => !item.isActive);
            }
        }

        // setFilteredAtivosDaUsina(filteredRows);
        return filteredRows;
    }

    // useEffect(() => {
    //     getTableItemsFiltered();
    // }, [statusFilter]);

    const allFilteredAtivosDaUsina = getTableItemsFiltered();
    const filteredAtivosDaUsina = allFilteredAtivosDaUsina.slice(0, 100);

    const getAtivoName = (nomeDoAtivo: string) => {
        const separatorIndex = nomeDoAtivo.indexOf("/");
        return separatorIndex >= 0 ? nomeDoAtivo.substring(separatorIndex + 1) : nomeDoAtivo;
    };

    const buildSupportMessage = (item: {
        planta: string;
        alarmCode: string;
        nomeDoAtivo: string;
        isActive: boolean;
        description: string;
    }) => {
        const status = item.isActive ? "Ativo" : "Inativo";

        return [
            "Acionamento de Suporte - Alarme Ativo",
            "",
            `Planta: ${item.planta}`,
            `Código: ${item.alarmCode}`,
            `Ativo: ${getAtivoName(item.nomeDoAtivo)}`,
            `Status: ${status}`,
            `Alarme: ${item.description}`,
            "",
            "Esse alarme já foi verificado?",
        ].join("\n");
    };

    const handleSupport = (item: {
        usinaPhoneNumber?: string;
        planta: string;
        alarmCode: string;
        nomeDoAtivo: string;
        isActive: boolean;
        description: string;
    }) => {
        if (!item.usinaPhoneNumber) {
            return;
        }

        const formattedPhone = item.usinaPhoneNumber.replace(/\D/g, "");
        const supportMessage = buildSupportMessage(item);
        const url = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(supportMessage)}`;

        window.open(url, "_blank");
    };

    const rows: TableRowType[] = filteredAtivosDaUsina.map((item) => {
        return {
            rowItems: [
                { item: item.planta, isMobile: true, align: "center" },
                { item: item.alarmCode, align: "center" },
                { item: getAtivoName(item.nomeDoAtivo), align: "center" },
                {
                    item: getStatusChip(item.status),
                    isMobile: true,
                    align: "center"
                },
                {
                    item: getActivityChip(item.isActive),
                    isMobile: true,
                    align: "center"
                },
                { item: item.description, align: "center" },
                {
                    item: formatDateTimeShort(item.ultimoAlarme),
                    align: "center"
                },
                {
                    item: formatDurationFromTimeSpan(item.tempoParado),
                    align: "center"
                },
                {
                    item: (
                        <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "center", width: "100%", gap: 1, marginTop: "6px" }}>
                            {item.usinaPhoneNumber && <BaseButton sx={{ padding: "4px 6px" }} onClick={() => handleSupport(item)} fontSize={"12px"} width={"auto"} heigth={"auto"} title="Acionar Suporte" />}
                            <BaseButton sx={{ padding: "4px 6px" }} onClick={() => goToServiceRequest(item)} fontSize={"12px"} width={"auto"} heigth={"auto"} title="Abrir OS" />
                            <BaseButton sx={{ padding: "4px 6px" }} onClick={() => gotToAlarmDetails(item.alarmCode)} fontSize={"12px"} width={"auto"} heigth={"auto"} title="Detalhes" />
                        </Box>
                    ),
                    isMobile: true,
                    align: "center"
                },
            ] as TableItem[],
        }
    }) || [];

    const handleExport = async () => {
        const exportDate = selectedExportDate ?? dayjs();
        const exportDateBR = exportDate.format("DD/MM/YYYY");
        const exportDateFileTag = exportDate.format("DD-MM-YYYY");
        const dateRequest = formatDateRequest(exportDate.toDate());

        const usinasToExport = selectedUsinaFilter
            ? availableUsinas.filter((usina) => usina.usinaIds.id === selectedUsinaFilter.id)
            : availableUsinas;

        if (usinasToExport.length === 0) {
            setToastNotifications((prev) => ({
                isAdding: true,
                notifications: [
                    ...prev.notifications,
                    {
                        text: "Nenhuma usina selecionada para exportação.",
                        severity: "warning",
                    },
                ],
            }));
            return;
        }

        setIsExportingHistory(true);
        try {
            const exportResults = await Promise.allSettled(
                usinasToExport.map((usina) =>
                    fetchOperationalDashboardHistoryRows(usina, dateRequest, exportDateBR),
                ),
            );
            const allRows = exportResults.flatMap((result) =>
                result.status === "fulfilled" ? result.value : [],
            );
            const failedUsinas = exportResults.filter((result) => result.status === "rejected").length;

            if (allRows.length === 0) {
                const allFailed =
                    exportResults.length > 0 &&
                    exportResults.every((result) => result.status === "rejected");
                setToastNotifications((prev) => ({
                    isAdding: true,
                    notifications: [
                        ...prev.notifications,
                        {
                            text: allFailed
                                ? "Não foi possível obter o histórico para a data selecionada. Verifique a conexão ou tente novamente."
                                : "Nenhum alarme retornado pelo servidor para essa data. Confira o dia no calendário ou outra usina no escopo.",
                            severity: allFailed ? "error" : "info",
                        },
                    ],
                }));
                return;
            }

            if (failedUsinas > 0) {
                setToastNotifications((prev) => ({
                    isAdding: true,
                    notifications: [
                        ...prev.notifications,
                        {
                            text: `Algumas usinas falharam na consulta (${failedUsinas}). Exportado apenas o que retornou com sucesso.`,
                            severity: "warning",
                        },
                    ],
                }));
            }

            const headerRow: string[] = [
                "Data de referência",
                "Planta",
                "Código",
                "Nome do ativo",
                "Severidade",
                "Status",
                "Descrição do alarme",
                "Último alarme",
                "Duração",
            ];

            downloadAtivosDaUsinaCsv(
                [headerRow, ...allRows],
                `ativos-da-usina-historico-${exportDateFileTag}`,
            );
            setIsExportDialogOpen(false);
        } catch {
            setToastNotifications((prev) => ({
                isAdding: true,
                notifications: [
                    ...prev.notifications,
                    {
                        text: "Erro ao exportar histórico do dia.",
                        severity: "error",
                    },
                ],
            }));
        } finally {
            setIsExportingHistory(false);
        }
    };

    const handleOpenExportDialog = () => {
        setSelectedExportDate(dayjs());
        setIsExportDialogOpen(true);
    };

    const handleCloseExportDialog = () => {
        if (!isExportingHistory) {
            setIsExportDialogOpen(false);
        }
    };

    const selectedUsinasAmount = selectedUsinaFilter ? 1 : availableUsinas.length;

    const selectedUsinasLabel = selectedUsinasAmount === 1 ? "1 usina selecionada" : `${selectedUsinasAmount} usinas selecionadas`;

    const selectedScopeLabel = selectedUsinaFilter
        ? `Escopo atual: ${selectedUsinaFilter.name}`
        : selectedCluster
            ? `Escopo atual: ${selectedCluster.name}`
            : selectedClientFilter
                ? `Escopo atual: ${selectedClientFilter.name}`
                : "Escopo atual: todas as usinas disponíveis";

    // const [view, setView] = useState<string | null>('table');

    // const handleToggleButton = (
    //     _event: React.MouseEvent<HTMLElement>,
    //     newAlignment: string | null,
    // ) => {
    //     if (newAlignment !== null) {
    //         setView(newAlignment);
    //     }
    // };
    return (
        centerOperationsData ? (
            <>
                <Screen.Container sx={{
                    paddingLeft: "10px",
                    paddingRight: "10px",
                    opacity: isUpdating ? 0.5 : undefined,
                }} >

                    {isUpdating && (
                        <Box>
                            {!centerOperationsData.isRopCard3001Loaded &&
                                !centerOperationsData.isRopCard3002Loaded
                                ? "Carregando Usinas e Alarmes..."
                                : !centerOperationsData.isRopCard3001Loaded
                                    ? "Carregando Usinas..."
                                    : !centerOperationsData.isRopCard3002Loaded
                                        ? "Carregando Alarmes..."
                                        : "Carregando..."}{" "}
                            <FontAwesomeIcon icon={faSpinner} spin />
                        </Box>
                    )}

                    <Grid container sx={{ paddingLeft: "6px", paddingRight: "2px", marginY: "14px", rowGap: "12px" }}>
                        {IGUData.map((item, index) => <KPIComponent key={index} {...item} />)}
                    </Grid>
                    <Grid container >
                        <Grid sx={{ paddingX: "12px" }} md={9} xs={12}>
                            <CardBox sx={{ justifyContent: "flex-start", backgroundColor: theme.cardBoxBackgroudColor, padding: "12px", borderRadius: "8px" }}>
                                <Box sx={{ display: "flex", marginTop: "12px", marginBottom: "8px", flexDirection: "row", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                                    <Typography
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: "15px"
                                        }}
                                    >
                                        Monitore e gerencie todos os ativos da usina em tempo real (Exibindo {filteredAtivosDaUsina.length} de {allFilteredAtivosDaUsina.length})
                                    </Typography>
                                    {/* <ToggleButtonGroup
                                              orientation="horizontal"
                                              value={view}
                                              exclusive
                                              sx={{ borderRadius: "8px", height: "36px", marginRight: "15px" }}
                                              onChange={handleToggleButton}
                                          >
                                              <ToggleButton value="table" aria-label="list">
                                                  <BackupTableIcon sx={{ marginRight: "8px" }} />
                                                  Tabela
                                              </ToggleButton>
                                              <ToggleButton value="map" aria-label="module">
                                                  <MapIcon sx={{ marginRight: "8px" }} />
                                                  Mapa
                                              </ToggleButton>
                                          </ToggleButtonGroup> */}
                                </Box>
                                <ToggleButtonGroup
                                    value={statusFilter}
                                    onChange={handleChangeStatusFilter}
                                    sx={{
                                        gap: 1,
                                        height: "36px",
                                        marginBottom: "8px",
                                        "& .MuiToggleButtonGroup-grouped": {
                                            border: "1px solid",
                                            borderRadius: "12px !important",
                                            margin: 0,
                                        },
                                    }}
                                >
                                    <ToggleButton
                                        value="ativo"
                                        sx={{
                                            textTransform: "none",
                                            borderColor: "#50D974",
                                            color: "#50D974",
                                            "&.Mui-selected": {
                                                backgroundColor: "#2e7d32",
                                                borderColor: "#2e7d32",
                                                color: "#fff",
                                                "&:hover": { backgroundColor: "#27642a", borderColor: "#2e7d32" },
                                            },
                                        }}
                                    >
                                        ● Ativo
                                    </ToggleButton>

                                    <ToggleButton
                                        value="inativo"
                                        sx={{
                                            textTransform: "none",
                                            borderColor: "#607D8B",
                                            color: "#90A4AE",
                                            "&.Mui-selected": {
                                                backgroundColor: "#455A64",
                                                borderColor: "#455A64",
                                                color: "#fff",
                                                "&:hover": { backgroundColor: "#37474F", borderColor: "#455A64" },
                                            },
                                        }}
                                    >
                                        ● Inativo
                                    </ToggleButton>

                                    <ToggleButton
                                        value="leve"
                                        sx={{
                                            textTransform: "none",
                                            borderColor: "#1976d2",
                                            color: "#2196f3",
                                            "&.Mui-selected": {
                                                backgroundColor: "#0d47a1",
                                                borderColor: "#0d47a1",
                                                color: "#fff",
                                                "&:hover": { backgroundColor: "#08306b", borderColor: "#0d47a1" },
                                            },
                                        }}
                                    >
                                        ● Leve
                                    </ToggleButton>

                                    <ToggleButton
                                        value="atencao"
                                        sx={{
                                            textTransform: "none",
                                            borderColor: "#FFC107",
                                            color: "#FFC107",
                                            "&.Mui-selected": {
                                                backgroundColor: "#FF8F00",
                                                borderColor: "#FF8F00",
                                                color: "#fff",
                                                "&:hover": { backgroundColor: "#FF6F00", borderColor: "#FF8F00" },
                                            },
                                        }}
                                    >
                                        ● Atenção
                                    </ToggleButton>

                                    <ToggleButton
                                        value="critico"
                                        sx={{
                                            textTransform: "none",
                                            borderColor: "#f44336",
                                            color: "#f44336",
                                            "&.Mui-selected": {
                                                backgroundColor: "#b71c1c",
                                                borderColor: "#b71c1c",
                                                color: "#fff",
                                                "&:hover": { backgroundColor: "#8b0000", borderColor: "#b71c1c" },
                                            },
                                        }}
                                    >
                                        ● Crítico
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                <RopTable
                                    search={search}
                                    setSearch={setSearch}
                                    searchPlaceholder="Pesquisar por ativo, planta ou tipo"
                                    hover
                                    isLoading={!centerOperationsData.isRopCard3002Loaded}
                                    onClickExportButton={() => handleOpenExportDialog()}
                                    itemsHeaders={
                                        [
                                            {
                                                item: "Planta",
                                                isMobile: true,
                                                align: "center",
                                            },
                                            {
                                                item: "Código",
                                                isMobile: false,
                                                align: "center",
                                            },
                                            {
                                                item: "Nome do ativo",
                                                align: "center",
                                            },
                                            {
                                                item: "Severidade",
                                                align: "center",
                                                isMobile: true,
                                            },
                                            {
                                                item: "Status",
                                                isMobile: false,
                                                align: "center",
                                            },
                                            {
                                                item: "Descrição do alarme",
                                                isMobile: false,
                                                align: "center",
                                            },
                                            {
                                                item: "Último alarme",
                                                isMobile: true,
                                                align: "center",
                                            },
                                            {
                                                item: "Duração",
                                                isMobile: false,
                                                align: "center",
                                            },
                                            {
                                                item: "Ações",
                                                align: "center",
                                                isMobile: true,
                                            },
                                        ]
                                    }
                                    itemRows={rows}
                                    sxCardBox={{ boxShadow: "none" }}
                                />
                            </CardBox>
                            <Box sx={{ backgroundColor: theme.cardBoxBackgroudColor, padding: "12px", borderRadius: "8px", marginTop: "12px" }}>
                                <Box sx={{ display: "flex", marginTop: "12px", marginBottom: "12px", marginLeft: "12px", flexDirection: "row", alignItems: "center" }}>
                                    <GoPulse style={{ fontSize: "26px", color: "#3F61E8" }} />
                                    <Typography
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: "16px",
                                            padding: "10px",
                                        }}
                                    >
                                        Monitore e gerencie todos os ativos da usina em tempo real
                                    </Typography>
                                </Box>
                                {equipmentInfos.map((item, index) => <EquipmentsStatus key={index} {...item} />)}
                            </Box>
                        </Grid>
                        <Grid container sx={{ display: "flex", flexDirection: "column", backgroundColor: theme.cardBoxBackgroudColor, padding: "12px", borderRadius: "8px" }} md={3} xs={12}>
                            {/* <Grid sx={{ paddingRight: "3px" }} item xs={12} md={6}>
                                      <Box sx={{ display: "flex", marginTop: "12px", marginBottom: "12px", marginLeft: "12px", flexDirection: "row", alignItems: "center", }}>
                                          <LuGauge style={{ fontSize: "26px", color: "#3F61E8" }} />
                                          <Typography
                                              sx={{
                                                  fontWeight: 500,
                                                  fontSize: "16px",
                                                  padding: "10px",
                                              }}
                                          >
                                              IGU - Índice de Gestão da Usina
                                          </Typography>
                                      </Box>
                                      <IGUScoreCard
                                          title="IGU Hoje"
                                          value={centerOperationsData?.iguScoreData.value}
                                          last7days={centerOperationsData?.iguScoreData.last7days}
                                          last30days={centerOperationsData?.iguScoreData.last30days}
                                          onActionClick={() => alert("Ação priorizada!")}
                                      />
                                      <Typography
                                          sx={{
                                              fontWeight: 400,
                                              fontSize: "14px",
                                              padding: "10px",
                                              color: "#8F969E",
                                              textAlign: "left"
                                          }}
                                      >
                                          Componentes do IGU
                                      </Typography>
                                      {IGUComponents.map((item, index) => <IGUComponentCard key={index} {...item} />)}
                                      <Typography
                                          sx={{
                                              fontWeight: 400,
                                              fontSize: "14px",
                                              padding: "10px",
                                              color: "#8F969E",
                                              textAlign: "left"
                                          }}
                                      >
                                          Top 3 Causas
                                      </Typography>
                                      {topCausesList.map((item, index) => <TopCausesCard key={index} {...item} />)}
                                  </Grid> */}
                            <Box sx={{ display: "flex", marginTop: "12px", marginBottom: "12px", marginLeft: "12px", flexDirection: "row", alignItems: "center", }}>
                                <MovingIcon style={{ fontSize: "26px", color: "#3F61E8" }} />
                                <Typography
                                    sx={{
                                        fontWeight: 500,
                                        fontSize: "16px",
                                        padding: "10px",
                                    }}
                                >
                                    Performance dos Ativos
                                </Typography>
                            </Box>
                            <Box sx={{ maxHeight: "600px", overflowY: "auto" }}>
                                {activePerformanceCards.map((card) => (
                                    <ActivePerformanceCard key={card.name} {...card} />
                                ))}
                            </Box>
                            <Typography
                                sx={{
                                    fontWeight: 500,
                                    fontSize: "14px",
                                    padding: "10px",
                                    textAlign: "left"
                                }}
                            >
                                Eventos Recentes
                            </Typography>
                            {recentEvents.map((item, index) => <RecentEventsCard key={index} {...item} />)}
                            {/* <Typography
                                      sx={{
                                          fontWeight: 500,
                                          fontSize: "14px",
                                          padding: "10px",
                                          textAlign: "left"
                                      }}
                                  >
                                      Sugestões do sistema
                                  </Typography>
                                  {suggestionEvents.map((item, index) => <SuggestionCard key={index} {...item} />)} */}
                        </Grid>
                    </Grid>
                </Screen.Container>
                <Dialog
                    open={isExportDialogOpen}
                    onClose={handleCloseExportDialog}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>Exportar histórico diário</DialogTitle>
                    <DialogContent>
                        <Typography sx={{ fontSize: "13px", color: "#8F969E", marginBottom: "8px" }}>
                            {selectedUsinasLabel}
                        </Typography>
                        <Typography sx={{ fontSize: "13px", color: "#8F969E", marginBottom: "16px" }}>
                            {selectedScopeLabel}
                        </Typography>
                        <Typography sx={{ fontSize: "12px", color: "#8F969E", marginBottom: "12px" }}>
                            O arquivo corresponde ao snapshot diário completo da data escolhida, com todas as criticidades. Os filtros da tabela (ex.: severidade) não alteram o conteúdo exportado.
                        </Typography>
                        <DatePickerLocalizationAdapter>
                            <DatePicker
                                label="Data"
                                format="DD/MM/YYYY"
                                value={selectedExportDate}
                                maxDate={dayjs()}
                                onChange={(date) => setSelectedExportDate(date)}
                                slotProps={{ textField: { size: "small", fullWidth: true } }}
                            />
                        </DatePickerLocalizationAdapter>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseExportDialog} disabled={isExportingHistory}>
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleExport}
                            disabled={!selectedExportDate || isExportingHistory || selectedUsinasAmount === 0}
                        >
                            {isExportingHistory ? "Exportando..." : "Exportar CSV"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        ) : (
            <Screen.Container>
                {/* <EmptyStateNotFound title="Nenhum dado encontrado" /> */}
                <Loading />
            </Screen.Container>
        ));
}